// Admin catalog management — the categories customers shop by and the cities
// LakayCart serves. The marketplace runs on this; seeded data was just the
// bootstrap. In-use rows can't be deleted (the API blocks it) — deactivate.

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { PortalShell } from '../../components/shared/PortalShell';
import { Card, SectionHeader } from '../../components/shared/primitives';
import { DataTable } from '../../components/shared/DataTable';
import { Button } from '../../components/stripe-ui/Button';
import { ADMIN_NAV } from '../_navs';
import { admin, type AdminCategory, type AdminCity } from '../../lib/api';

export function AdminCatalogPage() {
  return (
    <PortalShell nav={ADMIN_NAV} activeId="catalog" title="Catalog" subtitle="The shared item list merchants price against.">
      <div className="space-y-6">
        <Categories />
        <Cities />
      </div>
    </PortalShell>
  );
}

// ─── Categories ──────────────────────────────────────────────

function Categories() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['admin', 'categories'], queryFn: admin.categories });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'categories'] });

  const [slug, setSlug] = useState('');
  const [label, setLabel] = useState('');
  const [mode, setMode] = useState<'cart' | 'text'>('cart');

  const create = useMutation({
    mutationFn: () => admin.createCategory({ slug: slug.trim(), label: label.trim(), mode, active: true }),
    onSuccess: () => { setSlug(''); setLabel(''); invalidate(); toast.success('Category added'); },
    onError: (e: Error) => toast.error(e.message || 'Failed') });
  const toggle = useMutation({
    mutationFn: (c: AdminCategory) => admin.updateCategory(c.id, { active: !c.active }),
    onSuccess: invalidate,
    onError: (e: Error) => toast.error(e.message || 'Failed') });
  const remove = useMutation({
    mutationFn: (id: number) => admin.deleteCategory(id),
    onSuccess: () => { invalidate(); toast.success('Category deleted'); },
    onError: (e: Error) => toast.error(e.message || 'Cannot delete') });

  return (
    <div className="space-y-4">
      <Card padding="p-0">
      <div className="p-5 border-b border-[var(--color-hairline)]">
        <SectionHeader title="Service categories" subtitle="What customers shop by. 'Cart' shows an item grid; 'Text' is a free-form request." />
      </div>

      {/* Add row */}
      <div className="px-5 py-3 border-b border-[var(--color-hairline)] flex flex-wrap items-end gap-2 bg-[var(--color-panel)]/60">
        <Field label="Slug (immutable)" value={slug} onChange={(v) => setSlug(v.toLowerCase().replace(/[^a-z0-9_]/g, ''))} placeholder="electronics" w="w-36" />
        <Field label="Label" value={label} onChange={setLabel} placeholder="Electronics" w="w-44" />
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider font-semibold text-[var(--color-secondary-2)]">Mode</span>
          <select value={mode} onChange={(e) => setMode(e.target.value as 'cart' | 'text')} className="mt-1 block px-2 py-1.5 text-[13px] border border-[var(--color-border)] rounded-md bg-white">
            <option value="cart">Cart</option><option value="text">Text</option>
          </select>
        </label>
        <Button size="sm" onClick={() => create.mutate()} loading={create.isPending} disabled={!slug.trim() || !label.trim()} leadingIcon={<Plus className="w-3.5 h-3.5" />}>Add</Button>
      </div>

      </Card>

        <DataTable
          title="Service categories"
          subtitle="What customers shop by. 'Cart' shows an item grid; 'Text' is a free-form request."
          columns={[
            {
              key: 'label', header: 'Category', type: 'strong', width: 'minmax(0,1.4fr)',
              value: (c: AdminCategory) => (
                <>{c.label}<span className="ml-2 font-mono text-[11px] font-normal text-[var(--color-muted)]">{c.slug}</span></>
              ) },
            {
              key: 'mode', header: 'Mode', type: 'text', width: '110px',
              value: (c: AdminCategory) => c.mode },
            {
              key: 'usage', header: 'Usage', type: 'muted', width: 'minmax(0,1fr)',
              value: (c: AdminCategory) => `${c.item_count} items · ${c.request_count} requests` },
            {
              key: 'active', header: 'Active', type: 'node', width: '90px',
              value: (c: AdminCategory) => <Toggle on={c.active} onClick={() => toggle.mutate(c)} /> },
            {
              key: 'del', header: '', type: 'node', width: '70px',
              value: (c: AdminCategory) => {
                // A category with items or requests behind it cannot be
                // deleted without orphaning them — deactivating hides it from
                // customers and keeps the history intact.
                const inUse = c.item_count > 0 || c.request_count > 0;
                return (
                  <button
                    type="button"
                    onClick={() => { if (window.confirm(`Delete "${c.label}"?`)) remove.mutate(c.id); }}
                    disabled={inUse}
                    title={inUse ? 'In use — deactivate instead' : 'Delete'}
                    className="rounded p-1 text-[var(--color-muted)] hover:text-[var(--color-danger-fg)] disabled:opacity-30 disabled:hover:text-[var(--color-muted)]"
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                );
              } },
          ]}
          rows={q.data ?? []}
          rowKey={(c) => c.id}
          loading={q.isLoading}
          empty="No categories yet. Add the first one above."
        />
    </div>
  );
}

// ─── Cities ──────────────────────────────────────────────────

function Cities() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['admin', 'cities'], queryFn: admin.cities });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'cities'] });

  const [name, setName] = useState('');
  const [region, setRegion] = useState('');
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');

  const create = useMutation({
    mutationFn: () => admin.createCity({ name: name.trim(), region: region.trim() || null, country: 'HT', active: true, latitude: lat ? Number(lat) : null, longitude: lng ? Number(lng) : null }),
    onSuccess: () => { setName(''); setRegion(''); setLat(''); setLng(''); invalidate(); toast.success('City added'); },
    onError: (e: Error) => toast.error(e.message || 'Failed') });
  const toggle = useMutation({ mutationFn: (c: AdminCity) => admin.updateCity(c.id, { active: !c.active }), onSuccess: invalidate, onError: (e: Error) => toast.error(e.message) });
  const remove = useMutation({ mutationFn: (id: number) => admin.deleteCity(id), onSuccess: () => { invalidate(); toast.success('City deleted'); }, onError: (e: Error) => toast.error(e.message || 'Cannot delete') });

  return (
    <div className="space-y-4">
      <Card padding="p-0">
      <div className="p-5 border-b border-[var(--color-hairline)]">
        <SectionHeader title="Cities served" subtitle="Where VoyeLakay operates. Coordinates power nearest-merchant routing." />
      </div>

      <div className="px-5 py-3 border-b border-[var(--color-hairline)] flex flex-wrap items-end gap-2 bg-[var(--color-panel)]/60">
        <Field label="City name" value={name} onChange={setName} placeholder="Jacmel" w="w-40" />
        <Field label="Region" value={region} onChange={setRegion} placeholder="Sud-Est" w="w-36" />
        <Field label="Latitude" value={lat} onChange={setLat} placeholder="18.234" w="w-28" />
        <Field label="Longitude" value={lng} onChange={setLng} placeholder="-72.534" w="w-28" />
        <Button size="sm" onClick={() => create.mutate()} loading={create.isPending} disabled={!name.trim()} leadingIcon={<Plus className="w-3.5 h-3.5" />}>Add</Button>
      </div>

      </Card>

        <DataTable
          title="Cities served"
          subtitle="Where VoyeLakay operates. Coordinates power nearest-merchant routing."
          columns={[
            {
              key: 'name', header: 'City', type: 'strong', width: 'minmax(0,1.3fr)',
              value: (c: AdminCity) => (
                <>{c.name}{c.region && <span className="ml-2 text-[11px] font-normal text-[var(--color-muted)]">{c.region}</span>}</>
              ),
            },
            {
              key: 'coords', header: 'Coordinates', type: 'mono', width: '170px',
              value: (c: AdminCity) => (c.latitude && c.longitude
                ? `${Number(c.latitude).toFixed(3)}, ${Number(c.longitude).toFixed(3)}`
                : '—'),
            },
            {
              key: 'usage', header: 'Usage', type: 'muted', width: 'minmax(0,1fr)',
              value: (c: AdminCity) => `${c.merchant_count} merchants · ${c.recipient_count} recipients`,
            },
            {
              key: 'active', header: 'Active', type: 'node', width: '90px',
              value: (c: AdminCity) => <Toggle on={c.active} onClick={() => toggle.mutate(c)} />,
            },
            {
              key: 'del', header: '', type: 'node', width: '70px',
              value: (c: AdminCity) => {
                // Deleting a city with merchants or recipients in it would
                // orphan them; deactivating stops new orders instead.
                const inUse = c.merchant_count > 0 || c.recipient_count > 0;
                return (
                  <button
                    type="button"
                    onClick={() => { if (window.confirm(`Delete "${c.name}"?`)) remove.mutate(c.id); }}
                    disabled={inUse}
                    title={inUse ? 'In use — deactivate instead' : 'Delete'}
                    className="rounded p-1 text-[var(--color-muted)] hover:text-[var(--color-danger-fg)] disabled:opacity-30 disabled:hover:text-[var(--color-muted)]"
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                );
              },
            },
          ]}
          rows={q.data ?? []}
          rowKey={(c) => c.id}
          loading={q.isLoading}
          empty="No cities yet. Add one above."
        />
    </div>
  );
}

// ─── bits ────────────────────────────────────────────────────


function Field({ label, value, onChange, placeholder, w }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string; w: string }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider font-semibold text-[var(--color-secondary-2)]">{label}</span>
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={`mt-1 ${w} px-2 py-1.5 text-[13px] border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500/30`} />
    </label>
  );
}

function Toggle({ on, onClick }: { on: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${on ? 'bg-[var(--color-success-fg)]' : 'bg-[var(--color-border)]'}`} aria-pressed={on}>
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${on ? 'translate-x-4' : 'translate-x-0.5'}`} />
    </button>
  );
}
